import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Car, Hourglass, PackageCheck, Banknote, TrendingUp, Lock, Download, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { PlateChip } from '../components/PlateChip';
import { StatusPill } from '../components/StatusPill';
import { api, API, errMsg, isUpgradeErr } from '../lib/api';
import { peso } from '../lib/format';
import { useAuth } from '../lib/AuthContext';
import { useUpgrade, planLabel } from '../components/UpgradeModal';

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <Card className="rounded-2xl border-black/5 shadow-[var(--shadow-soft)]">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))]">
          <Icon className="h-4 w-4" />
          <span className="text-xs font-medium">{label}</span>
        </div>
        <div data-testid={testId} className="mt-2 text-2xl font-semibold font-display tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const { openUpgrade } = useUpgrade();
  const features = me?.shop?.features || {};
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');

  const load = async (m = month) => {
    try {
      const { data: d } = await api.get('/dashboard', { params: m ? { month: m } : {} });
      setData(d);
    } catch (e) {
      if (isUpgradeErr(e)) openUpgrade(planLabel(e.response.data.detail.required_plan));
      else toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const download = async (path, name) => {
    try {
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem('talyer_token')}` } });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const d = body.detail;
        if (d && d.code === 'plan_upgrade_required') { openUpgrade(planLabel(d.required_plan)); return; }
        throw new Error(d?.message || d || 'Export failed');
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${name} downloaded`);
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const lockedClick = () => openUpgrade('Elite');

  if (!data) {
    return <div className="grid grid-cols-2 md:grid-cols-3 gap-3">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>;
  }

  const c = data.cards;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-semibold font-display">Dashboard</h1>
        <div className="flex items-center gap-2">
          {features.needs_attention ? (
            <Input
              data-testid="dashboard-month-filter"
              type="month"
              value={month || c.month}
              onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
              className="h-10 rounded-xl w-[160px]"
            />
          ) : (
            <button data-testid="dashboard-month-filter-locked" onClick={lockedClick} className="flex items-center gap-1.5 h-10 rounded-xl border border-[hsl(var(--border))] px-3 text-sm text-[hsl(var(--muted-foreground))] opacity-70">
              <Lock className="h-3.5 w-3.5" /> Month filter
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={Car} label="Cars in shop" value={c.cars_in_shop} testId="dashboard-cars-in-shop" />
        <StatCard icon={Hourglass} label="Awaiting approval" value={c.awaiting_approval} testId="dashboard-awaiting-approval" />
        <StatCard icon={PackageCheck} label="Ready for release" value={c.ready_for_release} testId="dashboard-ready" />
        <StatCard icon={Banknote} label={`Collected — ${c.month}`} value={peso(c.collected_centavos)} testId="dashboard-collected" />
        <StatCard icon={TrendingUp} label="Pipeline value" value={peso(c.pipeline_centavos)} testId="dashboard-pipeline" />
      </div>

      <div className="flex gap-2 flex-wrap">
        {features.csv_export ? (
          <>
            <Button data-testid="dashboard-export-jobs-button" variant="outline" className="h-10 rounded-xl" onClick={() => download('/export/jobs.csv', 'jobs.csv')}>
              <Download className="h-4 w-4 mr-1.5" /> Export jobs CSV
            </Button>
            <Button data-testid="dashboard-export-payments-button" variant="outline" className="h-10 rounded-xl" onClick={() => download('/export/payments.csv', 'payments.csv')}>
              <Download className="h-4 w-4 mr-1.5" /> Export payments CSV
            </Button>
          </>
        ) : (
          <>
            <Button data-testid="dashboard-export-jobs-locked" variant="outline" className="h-10 rounded-xl opacity-70" onClick={lockedClick}>
              <Lock className="h-4 w-4 mr-1.5" /> Export jobs CSV
            </Button>
            <Button data-testid="dashboard-export-payments-locked" variant="outline" className="h-10 rounded-xl opacity-70" onClick={lockedClick}>
              <Lock className="h-4 w-4 mr-1.5" /> Export payments CSV
            </Button>
          </>
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-base font-semibold font-display flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600" /> Needs attention
          {!features.needs_attention && <Lock className="h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />}
        </h2>
        {features.needs_attention ? (
          data.needs_attention?.length ? (
            <div className="space-y-2">
              {data.needs_attention.map((n) => (
                <button
                  key={n.job_id}
                  data-testid="dashboard-needs-attention-item"
                  onClick={() => navigate(`/jobs/${n.job_id}`)}
                  className="w-full text-left rounded-2xl border border-black/5 bg-white p-3 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-card)] transition-shadow flex items-center gap-3"
                >
                  <PlateChip plate={n.plate} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{n.reason}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] font-plate">{n.jo_number}</div>
                  </div>
                  <StatusPill status={n.status} />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Nothing needs attention right now. Nice work.</p>
          )
        ) : (
          <button data-testid="dashboard-needs-attention-locked" onClick={lockedClick} className="w-full rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 text-sm text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-2 opacity-80">
            <Lock className="h-4 w-4" /> The "Needs attention" list is an Elite feature — tap to learn more
          </button>
        )}
      </div>
    </div>
  );
}
